import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Toastify, { notifyError } from "../../../utils/tostfy";
import MasterSuccessModal from '../MasterSuccessModal';
import { handleApiCall } from '../../../apis';
import { endpoint } from '../../../utils/endpoints';
import EditIcon from '../../../assets/image/edit-icon.svg'
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../../utils/search";
// import DeleteIcon2 from '../../../assets/image/delete-icon2.svg'  
import EyeIcon from '../../../assets/image/eye-icon.svg'
import LeftAngle from '../../../assets/image/left-angle.svg'
import RightAngle from '../../../assets/image/right-angle.svg'
import PermissionGate from '../../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../../utils/permissions';
import DeleteIcon from '../../../assets/image/delete-icon.svg'
import PageNumberInput from "../../../Components/Pagination/PageNumberInput";
/** Letters, digits, spaces only while typing */
const filterLocationInput = (value) => value.replace(/[^a-zA-Z0-9\s]/g, '');

/** Non-empty names: alphanumeric + spaces, not numbers-only */
const isLocationNameAllowed = (raw) => {
    const t = (raw || '').trim();
    if (!t) return true;
    if (/^\d+$/.test(t)) return false;
    return /^[a-zA-Z0-9\s]+$/.test(t);
};

const LOCATION_FORMAT_ERROR =
    'Only letters and numbers are allowed. Location names with only numbers are not allowed.';

const CompanyMaster = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [companies, setCompanies] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [addCompany, setAddCompany] = useState(false);
    const [editCompany, setEditCompany] = useState(false);
    const [addCompanyName, setAddCompanyName] = useState('');
    const [addLocations, setAddLocations] = useState(['']);
    const [addFormErrors, setAddFormErrors] = useState({
        name: '',
        locations: '',
        emptyLocationIndexes: [],
    });
    const [savingCompany, setSavingCompany] = useState(false);

    const [editingCompany, setEditingCompany] = useState(null);
    const [editCompanyName, setEditCompanyName] = useState('');
    /** @type {Array<{_id?: string, name: string, status: boolean}>} */
    const [editLocations, setEditLocations] = useState([{ name: '', status: true }]);
    const [editFormErrors, setEditFormErrors] = useState({
        name: '',
        locations: '',
        emptyLocationIndexes: [],
    });
    const [savingEdit, setSavingEdit] = useState(false);
    const [successModal, setSuccessModal] = useState({ open: false, message: '' });
    const openMasterSuccess = (message) =>
        setSuccessModal({ open: true, message: message || 'Saved successfully' });
    const closeMasterSuccess = () => setSuccessModal({ open: false, message: '' });

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const mapCompanyData = (list) =>
        (list || []).map((c) => ({
            _id: c._id,
            id: c._id,
            companyName: c.name,
            companyStatus: c.status,
            companyLocation: (c.location_data || []).map((l) => l.name),
            locationData: c.location_data || [],
        }));
        const searchForApi = (() => {
            const s = clampSearchInput(debouncedSearch).trim();
            if (!s) return '';
            if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
            return s;
        })();
    const fetchCompanyList = async () => {
        setListLoading(true);
        try {
            const q = `search=${encodeURIComponent(searchForApi)}&page=${currentPage}&limit=${itemsPerPage}`;
            const response = await handleApiCall('get', `${endpoint.listOfCompany}?${q}`, {});
            if (response?.data?.code === 200) {
                const d = response.data.data;
                setCompanies(mapCompanyData(d?.company_data));
                setTotalRecords(d?.total ?? 0);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message;
                if (errMsg) notifyError(errMsg);
            }
        } catch {
            notifyError('Failed to load companies');
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanyList();
    }, [debouncedSearch, currentPage, itemsPerPage]);

    const resetAddCompanyForm = () => {
        setAddCompanyName('');
        setAddLocations(['']);
        setAddFormErrors({ name: '', locations: '', emptyLocationIndexes: [] });
    };

    const openAddCompany = () => {
        resetAddCompanyForm();
        setAddCompany(true);
    };

    useEffect(() => {
        if (searchParams.get('openAdd') !== '1') return;
        openAddCompany();
    }, [searchParams]);

    const closeAddCompany = () => {
        setAddCompany(false);
        resetAddCompanyForm();
        if (searchParams.get('openAdd') === '1') {
            const next = new URLSearchParams(searchParams);
            next.delete('openAdd');
            setSearchParams(next, { replace: true });
        }
    };

    const validateAddCompany = () => {
        const e = { name: '', locations: '', emptyLocationIndexes: [] };
        const n = (addCompanyName || '').trim();
        if (!n) e.name = 'Company name is required';
        else if (n.length < 2) e.name = 'Company name must be at least 2 characters';
        else if (n.length > 120) e.name = 'Company name is too long';

        const trimmed = addLocations.map((x) => (x || '').trim());
        trimmed.forEach((loc, i) => {
            if (!loc) e.emptyLocationIndexes.push(i);
        });
        if (e.emptyLocationIndexes.length > 0) {
            e.locations = 'Please fill all location fields. Empty fields are not allowed.';
        } else if (trimmed.length < 1) {
            e.locations = 'Add at least one location';
        }
        if (!e.locations) {
            const long = trimmed.find((l) => l.length > 500);
            if (long) e.locations = 'Each location must be at most 500 characters';
        }
        if (!e.locations) {
            const bad = trimmed.find((l) => !isLocationNameAllowed(l));
            if (bad) e.locations = LOCATION_FORMAT_ERROR;
        }
        return { errors: e, name: n, location: trimmed };
    };

    const handleAddLocationRow = (e) => {
        e.preventDefault();
        setAddLocations((prev) => [...prev, '']);
    };

    const handleRemoveLocationRow = (index) => {
        setAddLocations((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
        if (addFormErrors.locations || addFormErrors.emptyLocationIndexes?.length) {
            setAddFormErrors((o) => ({ ...o, locations: '', emptyLocationIndexes: [] }));
        }
    };

    const handleAddCompanySubmit = async (e) => {
        e.preventDefault();
        const { errors, name, location: locationArr } = validateAddCompany();
        setAddFormErrors(errors);
        if (errors.name || errors.locations) return;

        setSavingCompany(true);
        try {
            const response = await handleApiCall('post', endpoint.addCompany, {
                name,
                location: locationArr,
            });
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Company added successfully');
                closeAddCompany();
                setCurrentPage(1);
                await fetchCompanyList();
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message;
            if (errMsg) notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingCompany(false);
        }
    };

    const resetEditCompanyForm = () => {
        setEditingCompany(null);
        setEditCompanyName('');
        setEditLocations([{ name: '', status: true }]);
        setEditFormErrors({ name: '', locations: '', emptyLocationIndexes: [] });
    };

    const openEditCompany = (row) => {
        setEditingCompany(row);
        setEditCompanyName(row.companyName || '');
        const locs = row.locationData?.length
            ? row.locationData.map((l) => ({
                  _id: l._id,
                  name: l.name || '',
                  status: l.status === 1 || l.status === true,
              }))
            : [{ name: '', status: true }];
        setEditLocations(locs);
        setEditFormErrors({ name: '', locations: '', emptyLocationIndexes: [] });
        setEditCompany(true);
    };

    const closeEditCompany = () => {
        setEditCompany(false);
        resetEditCompanyForm();
    };

    const validateEditCompany = () => {
        const e = { name: '', locations: '', emptyLocationIndexes: [] };
        const n = (editCompanyName || '').trim();
        if (!n) e.name = 'Company name is required';
        else if (n.length < 2) e.name = 'Company name must be at least 2 characters';
        else if (n.length > 120) e.name = 'Company name is too long';

        editLocations.forEach((row, i) => {
            if (!row._id && !(row.name || '').trim()) e.emptyLocationIndexes.push(i);
        });
        if (e.emptyLocationIndexes.length > 0) {
            e.locations = 'Please fill all location fields. Empty fields are not allowed.';
        }

        const locs = editLocations.map((x) => (x.name || '').trim()).filter(Boolean);
        if (!e.locations && locs.length < 1) e.locations = 'Add at least one location';
        if (!e.locations) {
            const long = locs.find((l) => l.length > 500);
            if (long) e.locations = 'Each location name must be at most 500 characters';
        }
        if (!e.locations) {
            const badNew = editLocations.find(
                (row) =>
                    !row._id &&
                    (row.name || '').trim() &&
                    !isLocationNameAllowed(row.name || '')
            );
            if (badNew) e.locations = LOCATION_FORMAT_ERROR;
        }
        return { errors: e };
    };

    const buildEditLocationPayload = () => {
        return editLocations
            .map((row) => {
                const n = (row.name || '').trim();
                if (!n) return null;
                if (row._id) {
                    return { _id: row._id, name: n, status: row.status ? 1 : 0 };
                }
                return { name: n };
            })
            .filter(Boolean);
    };

    const handleEditLocationAdd = (e) => {
        e.preventDefault();
        setEditLocations((prev) => [...prev, { name: '', status: true }]);
    };

    const handleEditLocationRemove = (index) => {
        setEditLocations((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
        if (editFormErrors.locations || editFormErrors.emptyLocationIndexes?.length) {
            setEditFormErrors((o) => ({ ...o, locations: '', emptyLocationIndexes: [] }));
        }
    };

    const handleEditCompanySubmit = async (e) => {
        e.preventDefault();
        if (!editingCompany?._id) {
            notifyError('No company selected');
            return;
        }
        const { errors: valErrors } = validateEditCompany();
        setEditFormErrors(valErrors);
        if (valErrors.name || valErrors.locations) return;

        const location = buildEditLocationPayload();
        if (location.length < 1) {
            setEditFormErrors((o) => ({ ...o, locations: 'Add at least one valid location' }));
            return;
        }

        setSavingEdit(true);
        try {
            const response = await handleApiCall('put', endpoint.editCompany, {
                company_id: editingCompany._id,
                name: (editCompanyName || '').trim(),
                location,
            });
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Company updated');
                closeEditCompany();
                await fetchCompanyList();
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message;
            if (errMsg) notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingEdit(false);
        }
    };

    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    return (
        <>
            <Toastify/>
            <div className="tab-pane-content md:mt-7 mt-3">

                <div className='flex gap-x-2.5 md:mb-4 mb-3'>
                    
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />

                    <PermissionGate module={MODULES.MASTER} action="add">
                        <button className="btn btn-danger ml-auto" onClick={openAddCompany}>
                            + Add New <span className='max-[767px]:hidden'>Copmany</span>
                        </button>
                    </PermissionGate>
                </div>

                {/* Table */}
                <div className="common-table-wrapper m-0">
                    <div className="overflow-x-auto">
                        <div className="table-responsive">
                            <table>
                                <thead>
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Company Name</th>
                                        <th>Company Location</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>

                                <tbody className='max-[767px]:[&_td]:!py-2'>
                                    {listLoading && (
                                        <tr>
                                            <td colSpan={4} className="text-center py-4 text-gray-500">
                                                Loading…
                                            </td>
                                        </tr>
                                    )}
                                    {!listLoading &&
                                        companies.map((driver, index) => (
                                        <tr key={driver._id || driver.id}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td>{driver.companyName}</td>
                                            <td>
                                                <div className='popoverBox'>
                                                    View Location
                                                    <div className='popInner'>
                                                        <ul>
                                                            {driver.companyLocation.map((loc, i) => (
                                                                <li key={i}>
                                                                    {loc}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex items-center justify-center gap-2">
                                                    <PermissionGate module={MODULES.MASTER} action="edit">
                                                        <button type="button" onClick={() => openEditCompany(driver)}>
                                                            <img src={EditIcon} alt="Edit" className="h-7 min-w-[22px]" />
                                                        </button>
                                                    </PermissionGate>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!listLoading && companies.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-center py-4 text-gray-500">
                                                No companies found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* 📄 Pagination */}
                <div className="flex gap-x-4 items-center md:mt-4 mt-2">

                    <select
                        className="p-2 shadow-sm outline-none max-[767px]:text-xs"
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                                ? '0 records found'
                                : `${displayFrom}–${displayTo} of ${totalRecords} records found`}
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage <= 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                                currentPage <= 1 ? '' : 'cursor-pointer'
                            }`}
                        >
                            <img src={LeftAngle} className="invert grayscale brightness-100" alt="Previous" />
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">of {totalPages || 1}</span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage >= totalPages || totalRecords === 0}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                                currentPage >= totalPages ? '' : 'cursor-pointer'
                            }`}
                        >
                            <img src={RightAngle} className="invert grayscale brightness-100" alt="Next" />
                        </button>

                    </div>
                </div>

            </div>

            {/* Add New Copmany Modal */}
            {addCompany && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-96 max-h-[90vh] overflow-y-auto">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Add New Company</h2>
                        <form
                            className="font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium"
                            onSubmit={handleAddCompanySubmit}
                            noValidate
                        >
                            <div className="form-group mb-3">
                                <label htmlFor="add-company-name">Add Company Name</label>
                                <input
                                    id="add-company-name"
                                    className="form-control"
                                    value={addCompanyName}
                                    onChange={(ev) => {
                                        setAddCompanyName(ev.target.value);
                                        if (addFormErrors.name) {
                                            setAddFormErrors((o) => ({ ...o, name: '' }));
                                        }
                                    }}
                                    disabled={savingCompany}
                                />
                                {addFormErrors.name && <p className="text-danger small mt-1 mb-0">{addFormErrors.name}</p>}
                            </div>
                            <div className="form-group mb-3">
                                <label>Add Company Locations</label>
                                {addLocations.map((loc, idx) => (
                                    <div key={idx} className="mb-2">
                                        <div className="flex gap-2 items-center">
                                            <div className="flex-1 min-w-0">
                                                <input
                                                    className={`form-control w-full${
                                                        addFormErrors.emptyLocationIndexes?.includes(idx)
                                                            ? ' border-danger'
                                                            : ''
                                                    }`}
                                                    value={loc}
                                                    placeholder="e.g. Delhi"
                                                    onChange={(ev) => {
                                                        const v = filterLocationInput(ev.target.value);
                                                        setAddLocations((prev) => {
                                                            const next = [...prev];
                                                            next[idx] = v;
                                                            return next;
                                                        });
                                                        if (
                                                            addFormErrors.locations ||
                                                            addFormErrors.emptyLocationIndexes?.includes(idx)
                                                        ) {
                                                            setAddFormErrors((o) => ({
                                                                ...o,
                                                                locations: '',
                                                                emptyLocationIndexes: (
                                                                    o.emptyLocationIndexes || []
                                                                ).filter((i) => i !== idx),
                                                            }));
                                                        }
                                                    }}
                                                    disabled={savingCompany}
                                                />
                                            </div>
                                            {addLocations.length > 1 ? (
                                                <button
                                                    type="button"
                                                    className="shrink-0 flex items-center justify-center border-0 bg-transparent p-0"
                                                    onClick={() => handleRemoveLocationRow(idx)}
                                                    disabled={savingCompany}
                                                    title="Remove location"
                                                >
                                                    <img src={DeleteIcon} alt="Delete" className="h-7 min-w-[22px]" />
                                                </button>
                                            ) : null}
                                        </div>
                                        {addFormErrors.emptyLocationIndexes?.includes(idx) && (
                                            <p className="text-danger small mt-1 mb-0">
                                                Location is required
                                            </p>
                                        )}
                                    </div>
                                ))}
                                {addFormErrors.locations && (
                                    <p className="text-danger small mt-1 mb-0">{addFormErrors.locations}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-danger !py-2 !px-4 !text-sm"
                                onClick={handleAddLocationRow}
                                disabled={savingCompany}
                            >
                                +Add More
                            </button>

                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeAddCompany}
                                    disabled={savingCompany}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingCompany}
                                >
                                    {savingCompany ? 'Saving…' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {editCompany && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-96 max-h-[90vh] overflow-y-auto">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Edit company</h2>
                        <form
                            className="font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium"
                            onSubmit={handleEditCompanySubmit}
                            noValidate
                        >
                            <div className="form-group mb-3">
                                <label htmlFor="edit-company-name">Company name</label>
                                <input
                                    id="edit-company-name"
                                    className="form-control"
                                    value={editCompanyName}
                                    onChange={(ev) => {
                                        setEditCompanyName(ev.target.value);
                                        if (editFormErrors.name) setEditFormErrors((o) => ({ ...o, name: '' }));
                                    }}
                                    disabled={savingEdit}
                                />
                                {editFormErrors.name && (
                                    <p className="text-danger small mt-1 mb-0">{editFormErrors.name}</p>
                                )}
                            </div>
                            <div className="form-group mb-3">
                                <label>Locations</label>
                                {editLocations.map((row, idx) => (
                                    <div key={row._id || `edit-loc-${idx}`} className="mb-3 p-2 rounded border border-gray-100">
                                        <div className="flex gap-2 items-center">
                                            <div className="flex-1 min-w-0">
                                                <input
                                                    className={`form-control w-full${row._id ? ' bg-light' : ''}${
                                                        editFormErrors.emptyLocationIndexes?.includes(idx)
                                                            ? ' border-danger'
                                                            : ''
                                                    }`}
                                                    value={row.name}
                                                    placeholder="Location name"
                                                    readOnly={!!row._id}
                                                    title={row._id ? 'Location name can’t be changed' : undefined}
                                                    onChange={
                                                        row._id
                                                            ? undefined
                                                            : (ev) => {
                                                                  const v = filterLocationInput(ev.target.value);
                                                                  setEditLocations((prev) => {
                                                                      const next = [...prev];
                                                                      next[idx] = { ...next[idx], name: v };
                                                                      return next;
                                                                  });
                                                                  if (
                                                                      editFormErrors.locations ||
                                                                      editFormErrors.emptyLocationIndexes?.includes(
                                                                          idx
                                                                      )
                                                                  ) {
                                                                      setEditFormErrors((o) => ({
                                                                          ...o,
                                                                          locations: '',
                                                                          emptyLocationIndexes: (
                                                                              o.emptyLocationIndexes || []
                                                                          ).filter((i) => i !== idx),
                                                                      }));
                                                                  }
                                                              }
                                                    }
                                                    disabled={savingEdit}
                                                />
                                            </div>
                                            {!row._id && (
                                                <button
                                                    type="button"
                                                    className="shrink-0 flex items-center justify-center border-0 bg-transparent p-0 disabled:opacity-50"
                                                    onClick={() => handleEditLocationRemove(idx)}
                                                    disabled={savingEdit || editLocations.length <= 1}
                                                    title="Remove location"
                                                >
                                                    <img src={DeleteIcon} alt="Delete" className="h-7 min-w-[22px]" />
                                                </button>
                                            )}
                                        </div>
                                        {editFormErrors.emptyLocationIndexes?.includes(idx) && (
                                            <p className="text-danger small mt-1 mb-0">
                                                Location is required
                                            </p>
                                        )}
                                        {row._id && (
                                            <div className="d-flex justify-content-end align-items-center gap-2 mt-2 text-sm text-secondary leading-none">
                                                <span>Active</span>
                                                <label
                                                    className={`position-relative d-inline-flex align-items-center rounded-pill my-0 cursor-pointer ${
                                                        row.status ? 'bg-success' : 'bg-danger'
                                                    }`}
                                                    style={{ width: 36, minWidth: 36, height: 20 }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="d-none"
                                                        checked={row.status}
                                                        onChange={(ev) => {
                                                            const c = ev.target.checked;
                                                            setEditLocations((prev) => {
                                                                const next = [...prev];
                                                                next[idx] = { ...next[idx], status: c };
                                                                return next;
                                                            });
                                                        }}
                                                        disabled={savingEdit}
                                                    />
                                                    <span
                                                        className="position-absolute top-50 translate-middle-y bg-white rounded-circle"
                                                        style={{
                                                            width: 16,
                                                            height: 16,
                                                            left: row.status ? 17 : 3,
                                                            transition: 'left 0.15s',
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {editFormErrors.locations && (
                                    <p className="text-danger small mt-1 mb-0">{editFormErrors.locations}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-danger !py-2 !px-4 !text-sm mb-3"
                                onClick={handleEditLocationAdd}
                                disabled={savingEdit}
                            >
                                +Add more
                            </button>

                            <div className="flex justify-center gap-3 mt-2">
                                <button
                                    type="button"
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeEditCompany}
                                    disabled={savingEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingEdit}
                                >
                                    {savingEdit ? 'Saving…' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <MasterSuccessModal
                open={successModal.open}
                message={successModal.message}
                onClose={closeMasterSuccess}
            />
        </>
    );
};

export default CompanyMaster;